import styles from "./MultiSelectFieldInput.module.scss";
import cx from "classnames";
import { Size } from "../../../../types";
import { Button } from "../../../../components/Button";
import { Chip } from "../../../../components/Chip";
import { Flex } from "../../../../components/Flex";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import { ReactElement, RefObject, useRef } from "react";
import { useMergeRefs } from "../../../../hooks/useMergeRefs/useMergeRefs";
import { MultiSelectFieldChipProps, MultiSelectFieldOption } from "../types";
import { useChipLayout } from "./useChipLayout";

export type MultiSelectFieldTriggerProps = {
  placeholder?: string;
  size?: Extract<Size, "small" | "medium" | "large">;
  disableClearButton?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  onClear?: () => void;
  id?: string;
  popoverTarget?: string;
  inputWrapperRef?: RefObject<HTMLDivElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleButtonProps?: Record<string, any>;
  prefix?: string | ReactElement;
  suffix?: string | ReactElement;
  selectedOptions?: MultiSelectFieldOption[];
  onRemoveOption?: (option: MultiSelectFieldOption) => void;
  singleRow?: boolean;
  maxChips?: number;
  getChipProps?: (option: MultiSelectFieldOption) => MultiSelectFieldChipProps;
};

/**
 * Non-searchable trigger for MultiSelectField when disableSearch is true.
 * Renders chips for selected items in a div-based trigger (no input element).
 */
export const MultiSelectFieldTrigger = ({
  placeholder,
  size,
  disableClearButton,
  disabled,
  readOnly,
  error,
  onClear,
  id,
  inputWrapperRef,
  toggleButtonProps,
  prefix,
  suffix,
  selectedOptions = [],
  onRemoveOption,
  singleRow,
  maxChips,
  getChipProps,
  popoverTarget,
}: MultiSelectFieldTriggerProps) => {
  const { ref: downshiftRef, ...restToggleButtonProps } =
    toggleButtonProps ?? {};
  const triggerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([inputWrapperRef, downshiftRef, triggerRef]);

  const { rowsRef, displayCount, hiddenCount } = useChipLayout({
    selectedOptionsLength: selectedOptions.length,
    singleRow,
    maxChips,
  });

  const inputWrapperClassNames = cx(styles["input-wrapper"], {
    [styles["small"]]: size === "small",
    [styles["large"]]: size === "large",
    [styles["no-clear-button"]]: disableClearButton,
    [styles["disabled"]]: disabled || readOnly,
  });

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mergedRef}
        {...restToggleButtonProps}
        // @ts-expect-error: popovertarget is a valid HTML attribute
        popovertarget={popoverTarget}
        id={id}
        className={cx(inputWrapperClassNames, restToggleButtonProps?.className)}
      >
        {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}

        <div className={styles["rows-wrapper"]} ref={rowsRef}>
          {selectedOptions.length > 0 && onRemoveOption
            ? selectedOptions.slice(0, displayCount).map((option, index) => (
                <div
                  key={`selected-item-${option.id}-${index}`}
                  className={styles["chip-wrapper"]}
                >
                  <Chip
                    {...getChipProps?.(option)}
                    label={option.label}
                    onClose={
                      disabled || readOnly
                        ? undefined
                        : (e) => {
                            e.stopPropagation();
                            onRemoveOption(option);
                            triggerRef.current?.focus();
                          }
                    }
                    className={styles["chip"]}
                    title={option.label}
                  />
                </div>
              ))
            : null}

          {hiddenCount > 0 ? (
            <div className={styles["chip-wrapper"]}>
              <Chip
                label={`+${hiddenCount}`}
                className={styles["chip"]}
                title={selectedOptions
                  .slice(displayCount)
                  .map((option) => option.label)
                  .join(", ")}
              />
            </div>
          ) : null}

          {selectedOptions.length === 0 ? (
            <Flex className={styles["input-flex"]} alignItems="center">
              <div
                className={cx(styles["input"], {
                  [styles["error"]]: error,
                })}
              >
                {placeholder}
              </div>
              {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
            </Flex>
          ) : null}
        </div>

        <div className={styles["buttons-wrapper"]}>
          <div className={styles["toggle-button-wrapper"]}>
            <Button
              icon={Chevron_Right}
              aria-label="toggle menu"
              appearance="ghost"
              size="small"
              disabled={disabled}
              tabIndex={-1}
              className={styles["toggle-button"]}
            />
          </div>
        </div>
      </div>
      {disableClearButton ? null : (
        <div
          className={styles["buttons-wrapper"]}
          style={{ insetInlineEnd: "2rem" }}
        >
          <div className={styles["close-button-wrapper"]}>
            <Button
              icon={Close}
              aria-label="clear selection"
              appearance="ghost"
              size="small"
              className={styles["close-button"]}
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

MultiSelectFieldTrigger.displayName = "MultiSelectFieldTrigger";
